/**
 * HandScanRitual.tsx
 *
 * Full-screen immersive overlay that simulates a "hand scan" ritual before
 * shuffling the tarot deck.
 *
 * ## State machine
 *
 * The component progresses through the following states:
 *
 * ```
 * IDLE
 *   └─ (mount + 50 ms) ──► SCANNING
 *                               │
 *                   (touch/pointer detected)
 *                               │
 *                               ▼
 *                         ALL_DETECTED
 *                               │
 *                           (2.2 s)
 *                               │
 *                               ▼
 *                         SCANNING_RED
 *                               │
 *                           (5.2 s)
 *                               │
 *                               ▼
 *                          CARD_SHUFFLE  ◄─ cycles through SHUFFLE_MESSAGES
 *                               │
 *                     (all messages shown)
 *                               │
 *                               ▼
 *                         REVEAL_READING
 *                               │
 *                           (0.6 s)
 *                               │
 *                               ▼
 *                           onComplete()
 * ```
 *
 * ## Touch / pointer detection
 * Hand detection is triggered by any of:
 * - Any single touch held for ≥ 2 seconds (fallback timer).
 * - Three or more simultaneous touches held for ≥ 1.5 seconds.
 * - Three or more fingers detected via `touchmove` after initially fewer.
 *
 * Haptic feedback is provided via `navigator.vibrate` where available.
 *
 * ## Animations
 * - **Green scan line**: a ping-pong (top → bottom → top) CSS animation during
 *   `SCANNING_GREEN` and `HAND_DETECTED`.
 * - **Red scan line**: a one-shot downward sweep during `SCANNING_RED`.
 * - **Card shuffle**: a fan of randomly selected card thumbnails with staggered
 *   entrance animations during `CARD_SHUFFLE`.
 *
 * @prop onComplete - Called after the reveal animation finishes; the parent should
 *                    dismiss this overlay and apply the shuffled calendar.
 * @prop onClose    - Called when the user taps the × close button to abort the ritual.
 */

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TAROT_CARDS } from '@/data/tarotData';
import { useTheme } from '@/lib/themeContext';

/**
 * All possible states of the hand-scan ritual state machine.
 *
 * - `IDLE`           — Component just mounted; not yet animating.
 * - `SCANNING`       — Five ellipses shown; red scan line sweeping; waiting for all 5 touches.
 * - `ALL_DETECTED`   — All five finger positions have active touches; brief confirmation.
 * - `CARD_SHUFFLE`   — Card fan animation with cycling status messages.
 * - `REVEAL_READING` — Final fade-out before `onComplete` is called.
 */
type ScanState = 'IDLE' | 'SCANNING' | 'ALL_DETECTED' | 'CARD_SHUFFLE' | 'REVEAL_READING';

/** Props accepted by `HandScanRitual`. */
interface HandScanRitualProps {
  /** Called after the full ritual animation completes successfully. */
  onComplete: () => void;
  /** Called when the user taps the × button to abort the ritual early. */
  onClose: () => void;
}

/** Status messages cycled through during the card-shuffle animation phase. */
const SHUFFLE_MESSAGES = [
  "Aligning with today\'s date...",
  'Calculating card positions...',
  'Revealing your reading...',
];

/**
 * Finger ellipse definitions.
 * Positions are expressed as percentages of the container width/height.
 * Order: index 0 = thumb (bottom-right), 1 = index, 2 = middle, 3 = ring, 4 = pinky (top-right).
 * Matches the reference image layout.
 */
interface EllipseDef {
  /** Finger label */
  label: string;
  /** Centre X as % of container width */
  cx: number;
  /** Centre Y as % of container height */
  cy: number;
  /** Horizontal radius in px */
  rx: number;
  /** Vertical radius in px */
  ry: number;
}

const ELLIPSES: EllipseDef[] = [
  // pinky — top-right
  { label: 'pinky',  cx: 72, cy: 18, rx: 40, ry: 40 },
  // ring — upper-left
  { label: 'ring',   cx: 38, cy: 28, rx: 55, ry: 55 },
  // middle — centre-left
  { label: 'middle', cx: 30, cy: 46, rx: 55, ry: 55 },
  // index — lower-left
  { label: 'index',  cx: 36, cy: 64, rx: 55, ry: 55 },
  // thumb — bottom-right
  { label: 'thumb',  cx: 74, cy: 79, rx: 55, ry: 55 },
];

/**
 * Select a random subset of tarot cards for the shuffle animation fan.
 */
function getShuffleCards(deckIndex: number, count = 8): { id: string; src: string; name: string }[] {
  const shuffled = [...TAROT_CARDS].sort(() => Math.random() - 0.5).slice(0, count);
  return shuffled.map(c => ({
    id: c.id,
    src: c.imageUrl[deckIndex] ?? c.imageUrl[0],
    name: c.name,
  }));
}

/**
 * Returns true if the touch point (clientX, clientY) is inside the ellipse
 * defined by its centre (cx%, cy% of container) and radii (px).
 */
function isTouchInEllipse(
  touchX: number,
  touchY: number,
  ellipse: EllipseDef,
  containerRect: DOMRect,
): boolean {
  const cx = containerRect.left + (ellipse.cx / 100) * containerRect.width;
  const cy = containerRect.top + (ellipse.cy / 100) * containerRect.height;
  const dx = touchX - cx;
  const dy = touchY - cy;
  // Ellipse equation: (dx/rx)^2 + (dy/ry)^2 <= 1
  return (dx * dx) / (ellipse.rx * ellipse.rx) + (dy * dy) / (ellipse.ry * ellipse.ry) <= 1;
}

export default function HandScanRitual({ onComplete, onClose }: HandScanRitualProps) {
  const { deckIndex } = useTheme();
  const [scanState, setScanState] = useState<ScanState>('IDLE');
  const [visible, setVisible] = useState(false);
  const [flipped, setFlipped] = useState(false);

  /** Which ellipses currently have an active touch (by index 0-4) */
  const [activeEllipses, setActiveEllipses] = useState<boolean[]>([false, false, false, false, false]);

  const [shuffleMessageIndex, setShuffleMessageIndex] = useState(0);
  const [shuffleCards, setShuffleCards] = useState<{ id: string; src: string; name: string }[]>([]);
  const [fadeToShuffle, setFadeToShuffle] = useState(false);

  const scanStateRef = useRef<ScanState>('IDLE');
  const containerRef = useRef<HTMLDivElement>(null);
  const allDetectedRef = useRef(false);
  const shuffleTransitionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shuffleRevealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep ref in sync
  useEffect(() => {
    scanStateRef.current = scanState;
  }, [scanState]);

  // Fade in on mount
  useEffect(() => {
    let t = setTimeout(() => {
      setVisible(true);
      setScanState('SCANNING');
    }, 50);
    return () => clearTimeout(t);
  }, []);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const beginShuffleTransition = useCallback((delay = 700) => {
    if (shuffleTransitionTimerRef.current) {
      clearTimeout(shuffleTransitionTimerRef.current);
      shuffleTransitionTimerRef.current = null;
    }
    if (shuffleRevealTimerRef.current) {
      clearTimeout(shuffleRevealTimerRef.current);
      shuffleRevealTimerRef.current = null;
    }

    shuffleTransitionTimerRef.current = setTimeout(() => {
      setFadeToShuffle(true);
      shuffleRevealTimerRef.current = setTimeout(() => {
        const cards = getShuffleCards(deckIndex, 9);
        setShuffleCards(cards);
        setScanState('CARD_SHUFFLE');
        setShuffleMessageIndex(0);
        setFadeToShuffle(false);
        shuffleTransitionTimerRef.current = null;
        shuffleRevealTimerRef.current = null;
      }, 600);
    }, delay);
  }, [deckIndex]);

  useEffect(() => {
    return () => {
      if (shuffleTransitionTimerRef.current) {
        clearTimeout(shuffleTransitionTimerRef.current);
      }
      if (shuffleRevealTimerRef.current) {
        clearTimeout(shuffleRevealTimerRef.current);
      }
    };
  }, []);

  // Watch for all 5 ellipses active → transition to card shuffle
  useEffect(() => {
    if (scanState !== 'SCANNING') return;
    const allActive = activeEllipses.every(Boolean);
    if (!allActive || allDetectedRef.current) return;
    allDetectedRef.current = true;

    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([80, 60, 120]);
    }

    setScanState('ALL_DETECTED');
    beginShuffleTransition(700);
  }, [activeEllipses, scanState, beginShuffleTransition]);

  // Cycle through shuffle messages
  useEffect(() => {
    if (scanState !== 'CARD_SHUFFLE') return;
    if (shuffleMessageIndex >= SHUFFLE_MESSAGES.length - 1) {
      let t = setTimeout(() => {
        setScanState('REVEAL_READING');
        setTimeout(() => {
          onComplete();
        }, 600);
      }, 1800);
      return () => clearTimeout(t);
    }
    let t = setTimeout(() => {
      setShuffleMessageIndex(i => i + 1);
    }, 1600);
    return () => clearTimeout(t);
  }, [scanState, shuffleMessageIndex, onComplete]);

  /**
   * Recompute which ellipses have active touches from the current TouchList.
   */
  const updateActiveEllipses = useCallback((touches: React.TouchList | TouchList) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const next: boolean[] = [false, false, false, false, false];
    for (let t = 0; t < touches.length; t++) {
      const touch = touches[t];
      for (let e = 0; e < ELLIPSES.length; e++) {
        const el = flipped
          ? { ...ELLIPSES[e], cy: 100 - ELLIPSES[e].cy }
          : ELLIPSES[e];
        if (isTouchInEllipse(touch.clientX, touch.clientY, el, rect)) {
          next[e] = true;
        }
      }
    }
    setActiveEllipses(prev => {
      // Haptic feedback for each newly activated ellipse
      const newlyActivated = next.filter((active, i) => active && !prev[i]).length;
      if (newlyActivated > 0 && typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(newlyActivated > 1 ? [30, 20, 30] : 30);
      }
      return next;
    });
  }, [flipped]);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (scanStateRef.current !== 'SCANNING') return;
      e.preventDefault();
      updateActiveEllipses(e.touches);
    },
    [updateActiveEllipses],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (scanStateRef.current !== 'SCANNING') return;
      e.preventDefault();
      updateActiveEllipses(e.touches);
    },
    [updateActiveEllipses],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (scanStateRef.current !== 'SCANNING') return;
      updateActiveEllipses(e.touches);
    },
    [updateActiveEllipses],
  );

  const isScanning = scanState === 'SCANNING' || scanState === 'ALL_DETECTED';
  const isCardShuffle = scanState === 'CARD_SHUFFLE';
  const isReveal = scanState === 'REVEAL_READING';

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[200] select-none overflow-hidden"
      style={{
        background: '#000',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.5s ease',
        /* Force portrait by constraining to viewport */
        touchAction: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── SCAN SCREEN ── */}
      {isScanning && (
        <div
          className="absolute inset-0 overflow-hidden"
          style={{
            opacity: fadeToShuffle ? 0 : 1,
            transition: fadeToShuffle ? 'opacity 0.6s ease' : 'none',
          }}
        >
          {/* Subtle dark background */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at 50% 50%, rgba(30,58,138,0.08) 0%, #000 70%)',
            }}
          />

          {/* Instruction text — vertical on left side */}
          <div
            className="absolute left-0 top-0 bottom-0 flex flex-col items-center justify-center z-10 pointer-events-none"
            style={{ width: 40 }}
          >
            <div
              style={{
                transform: 'rotate(-90deg)',
                whiteSpace: 'nowrap',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <p
                className="font-display tracking-widest uppercase text-sm"
                style={{
                  color: 'rgba(99,179,237,0.85)',
                  textShadow: '0 0 12px rgba(99,179,237,0.5)',
                  letterSpacing: '0.18em',
                }}
              >
                {scanState === 'ALL_DETECTED' ? 'Hand scan complete' : 'Place your fingers on the circles'}
              </p>
              {scanState !== 'ALL_DETECTED' && (
                <p
                  className="font-body text-xs"
                  style={{ color: 'rgba(99,179,237,0.4)' }}
                >
                  {activeEllipses.filter(Boolean).length} / 5 detected
                </p>
              )}
            </div>
          </div>

          {/* Five ellipses rendered as SVG overlay */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{ zIndex: 4 }}
          >
            {ELLIPSES.map((el, i) => {
              const active = activeEllipses[i];
              const cy = flipped ? 100 - el.cy : el.cy;
              return (
                <ellipse
                  key={el.label}
                  cx={`${el.cx}%`}
                  cy={`${cy}%`}
                  /* rx/ry need to be in SVG user-units; since viewBox is 0-100 and
                     preserveAspectRatio=none, we convert px to % of 100-unit space.
                     We'll use a separate HTML layer for the actual ellipses so that
                     px sizing is accurate. This SVG layer is just for the scan line clip. */
                  rx={`${(el.rx / 390) * 100}`}
                  ry={`${(el.ry / 844) * 100}`}
                  fill="none"
                  stroke="none"
                />
              );
            })}
          </svg>

          {/* Ellipses as HTML divs for accurate px sizing */}
          {ELLIPSES.map((el, i) => {
            const active = activeEllipses[i];
            const locked = scanState === 'ALL_DETECTED';
            const cy = flipped ? 100 - el.cy : el.cy;
            return (
              <div
                key={el.label}
                className="absolute"
                style={{
                  left: `calc(${el.cx}% - ${el.rx}px)`,
                  top: `calc(${cy}% - ${el.ry}px)`,
                  width: el.rx * 2,
                  height: el.ry * 2,
                  borderRadius: '50%',
                  border: locked
                    ? '2.5px solid #93c5fd'
                    : `2.5px solid ${active ? '#60a5fa' : '#3b82f6'}`,
                  boxShadow: locked
                    ? '0 0 32px 10px rgba(147,197,253,0.75), 0 0 64px 20px rgba(96,165,250,0.4), inset 0 0 20px rgba(147,197,253,0.25)'
                    : active
                    ? '0 0 18px 4px rgba(96,165,250,0.55), inset 0 0 12px rgba(96,165,250,0.15)'
                    : '0 0 8px 1px rgba(59,130,246,0.3)',
                  background: locked
                    ? 'rgba(147,197,253,0.14)'
                    : active
                    ? 'rgba(96,165,250,0.08)'
                    : 'rgba(59,130,246,0.03)',
                  zIndex: 5,
                  // Freeze animation when locked; pulse when active
                  animation: locked ? 'none' : active ? 'ellipsePulse 1.1s ease-in-out infinite' : 'none',
                  transition: 'border-color 0.3s, box-shadow 0.4s, background 0.3s, top 0.4s ease',
                  pointerEvents: 'none',
                }}
              />
            );
          })}

          {/* Red scan line — ping-pong top→bottom→top, continuous until all detected */}
          {scanState === 'SCANNING' && (
            <div
              className="absolute left-0 right-0 h-0.5 hand-scan-line-red-new"
              style={{ zIndex: 6 }}
            />
          )}

          {/* All detected — success glow overlay */}
          {scanState === 'ALL_DETECTED' && (
            <div
              className="absolute inset-0 pointer-events-none z-10"
              style={{
                background: 'radial-gradient(ellipse at 50% 50%, rgba(147,197,253,0.18) 0%, rgba(96,165,250,0.08) 40%, transparent 70%)',
                animation: 'successGlowPulse 1.5s ease-in-out forwards',
              }}
            />
          )}

          {/* Flip hand button — top-left, same height as close button */}
          <button
            className="absolute top-4 left-4 z-30 w-8 h-8 flex items-center justify-center transition-colors"
            style={{ color: flipped ? 'rgba(96,165,250,0.9)' : 'rgba(255,255,255,0.4)' }}
            onClick={() => {
              setFlipped(f => !f);
              setActiveEllipses([false, false, false, false, false]);
              allDetectedRef.current = false;
              if (scanState === 'ALL_DETECTED') setScanState('SCANNING');
            }}
            aria-label={flipped ? 'Switch to right hand' : 'Switch to left hand'}
            title={flipped ? 'Right hand' : 'Left hand'}
          >
            {/* Two circular arrows icon (↻↺ style) */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
            >
              {/* Top arc arrow (clockwise) */}
              <path d="M4 8a8 8 0 0 1 14.5-2.5" />
              <polyline points="20 2 20 6 16 6" />
              {/* Bottom arc arrow (counter-clockwise) */}
              <path d="M20 16a8 8 0 0 1-14.5 2.5" />
              <polyline points="4 22 4 18 8 18" />
            </svg>
          </button>

          {/* Close button */}
          <button
            className="absolute top-4 right-4 z-30 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white/80 transition-colors text-xl"
            onClick={onClose}
            aria-label="Close hand scan"
          >
            ×
          </button>

          {/* Fallback "Continue" button — vertical on right side */}
          {scanState === 'SCANNING' && (
            <button
              className="absolute right-0 top-0 bottom-0 z-20 flex items-center justify-center"
              style={{
                width: 44,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (allDetectedRef.current) return;
                allDetectedRef.current = true;
                setScanState('ALL_DETECTED');
                beginShuffleTransition(1600);
              }}
            >
              <span
                className="font-display text-xs tracking-widest uppercase px-3 py-2 border transition-all duration-200 hover:bg-white/10 active:scale-95"
                style={{
                  transform: 'rotate(-90deg)',
                  display: 'inline-block',
                  whiteSpace: 'nowrap',
                  borderColor: 'rgba(255,255,255,0.25)',
                  color: 'rgba(255,255,255,0.45)',
                }}
              >
                Continue Scan
              </span>
            </button>
          )}
        </div>
      )}

      {/* ── CARD SHUFFLE SCREEN ── */}
      {(isCardShuffle || isReveal) && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden"
          style={{
            opacity: isReveal ? 0 : 1,
            transition: isReveal ? 'opacity 0.6s ease' : 'none',
          }}
        >
          {/* Mystical background glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at 50% 60%, rgba(201,168,76,0.08) 0%, rgba(88,28,135,0.12) 40%, transparent 70%)',
            }}
          />

          {/* Floating particles */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={`particle-${i}`}
                className="absolute rounded-full hand-scan-particle"
                style={{
                  width: Math.random() * 3 + 1,
                  height: Math.random() * 3 + 1,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  background:
                    i % 3 === 0 ? '#c9a84c' : i % 3 === 1 ? '#a78bfa' : '#10b981',
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${2 + Math.random() * 3}s`,
                }}
              />
            ))}
          </div>

          {/* Message at top */}
          <div className="relative z-10 mb-6 px-8 text-center" style={{ minHeight: 40 }}>
            <p
              key={`msg-${shuffleMessageIndex}`}
              className="font-display tracking-widest uppercase text-sm hand-scan-message-fade"
              style={{
                color: '#c9a84c',
                textShadow: '0 0 16px rgba(201,168,76,0.8)',
                letterSpacing: '0.15em',
              }}
            >
              {SHUFFLE_MESSAGES[shuffleMessageIndex]}
            </p>
          </div>

          {/* Shuffling cards */}
          <div
            className="relative z-10 w-full flex items-center justify-center"
            style={{ height: 280 }}
          >
            {shuffleCards.map((card, i) => (
              <div
                key={`shuffle-${card.id}-${i}`}
                className="absolute hand-scan-card"
                style={{
                  width: 80,
                  height: 130,
                  animationDelay: `${i * 0.15}s`,
                  '--card-index': i,
                  '--card-total': shuffleCards.length,
                } as React.CSSProperties}
              >
                <div
                  className="w-full h-full rounded-lg overflow-hidden border-2 shadow-2xl"
                  style={{
                    borderColor: 'rgba(201,168,76,0.5)',
                    boxShadow:
                      '0 0 20px rgba(201,168,76,0.3), 0 8px 32px rgba(0,0,0,0.8)',
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={card.src}
                    alt={`Tarot card: ${card.name}`}
                    className="w-full h-full object-cover object-center"
                    draggable={false}
                  />
                </div>
                <div
                  className="absolute inset-0 rounded-lg pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse at 50% 30%, rgba(201,168,76,0.15) 0%, transparent 70%)',
                  }}
                />
              </div>
            ))}
          </div>

          {/* Gold divider */}
          <div
            className="relative z-10 mt-6 w-48"
            style={{
              height: 1,
              background:
                'linear-gradient(90deg, transparent, rgba(201,168,76,0.6), transparent)',
            }}
          />

          {/* Subtitle */}
          <p
            className="relative z-10 mt-4 font-body text-xs tracking-wider italic"
            style={{ color: 'rgba(201,168,76,0.5)' }}
          >
            ✦ The cards are aligning ✦
          </p>
        </div>
      )}

      {/* Fade-to-shuffle transition overlay */}
      {fadeToShuffle && (
        <div
          className="absolute inset-0 z-50 pointer-events-none"
          style={{ background: '#000', opacity: 1, transition: 'opacity 0.6s ease' }}
        />
      )}

      <style>{`
        @keyframes handScanLineRedNew {
          0%   { top: 0%; }
          50%  { top: 100%; }
          100% { top: 0%; }
        }
        @keyframes ellipsePulse {
          0%   { transform: scale(1);    opacity: 1; }
          50%  { transform: scale(1.4);  opacity: 0.75; }
          100% { transform: scale(1);    opacity: 1; }
        }
                @keyframes successGlowPulse {
          0%   { opacity: 0; }
          20%  { opacity: 1; }
          70%  { opacity: 1; }
          100% { opacity: 0.6; }
        }
        @keyframes handScanParticle {
          0%   { transform: translateY(0) scale(1); opacity: 0.8; }
          50%  { opacity: 1; }
          100% { transform: translateY(-40px) scale(0); opacity: 0; }
        }
        @keyframes handScanCardShuffle {
          0%   { transform: translateX(0) translateY(0) rotate(0deg) scale(0.8); opacity: 0; }
          15%  { opacity: 1; }
          40%  { transform: translateX(calc((var(--card-index, 0) - var(--card-total, 1) / 2) * 55px)) translateY(-20px) rotate(calc((var(--card-index, 0) - var(--card-total, 1) / 2) * 8deg)) scale(1); }
          60%  { transform: translateX(calc((var(--card-index, 0) - var(--card-total, 1) / 2) * 55px)) translateY(-20px) rotate(calc((var(--card-index, 0) - var(--card-total, 1) / 2) * 8deg)) scale(1); }
          80%  { transform: translateX(calc((var(--card-index, 0) - var(--card-total, 1) / 2) * 30px)) translateY(10px) rotate(calc((var(--card-index, 0) - var(--card-total, 1) / 2) * 4deg)) scale(0.95); opacity: 1; }
          100% { transform: translateX(0) translateY(0) rotate(0deg) scale(0.9); opacity: 0.9; }
        }
        @keyframes handScanMessageFade {
          0%   { opacity: 0; transform: translateY(8px); }
          20%  { opacity: 1; transform: translateY(0); }
          80%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0.8; transform: translateY(0); }
        }
        .hand-scan-line-red-new {
          background: linear-gradient(90deg, transparent 0%, rgba(220,38,38,0.3) 20%, #dc2626 50%, rgba(220,38,38,0.3) 80%, transparent 100%);
          box-shadow: 0 0 8px 2px rgba(220,38,38,0.6), 0 0 20px 4px rgba(220,38,38,0.3);
          animation: handScanLineRedNew 5s ease-in-out infinite;
        }
        .hand-scan-particle {
          animation: handScanParticle 2s ease-out infinite;
        }
        .hand-scan-card {
          animation: handScanCardShuffle 3s ease-in-out infinite;
        }
        .hand-scan-message-fade {
          animation: handScanMessageFade 1.6s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
}
